import React, { useMemo } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import pathfinder from '@fiverr-private/pathfinder';
import { SellerPageFederatedSeller, useSellerData } from '@fiverr-private/seller_pages_toolbox';
import { logger } from '@fiverr-private/obs';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { ReviewsCarousel } from '@fiverr-private/reviews';
import { useSellerReviews } from '../../hooks/useSellerReviews';
import { PAGE_NAME } from '../../../../configuration/constants';
import { ReviewsSkeleton } from './ReviewsSkeleton';

const onRenderError = (originalError) => {
    const err = new Error(`Failed while rendering the HighlightedReviews Component`);
    logger.error(err, {
        cause: originalError,
    });
};

interface HighlightedReviewsProps {
    areReviewsLoading: boolean;
}

export const HighlightedReviews = ({ areReviewsLoading }: HighlightedReviewsProps) => {
    const { pageCtxId } = getContext();
    const { approvedGigsCount } = useSellerData(selectSellerSlice);
    const reviews = useSellerReviews();

    const reviewsKey = useMemo(() => reviews?.buying_reviews.reviews.map((review) => review.id).join('-'), [reviews]);

    const showBuyingReviews = reviews && reviews.buying_reviews?.reviews?.length > 0 && approvedGigsCount > 0;

    const reviewsBaseConfig = {
        pathFinderConfig: {
            path: 'seller_page_reviews',
        },
        pageCtxId,
        pageName: PAGE_NAME,
        biEventGroup: 'seller_page',
        reviews: reviews?.buying_reviews.reviews,
    };

    const reviewsTranslationUrl = pathfinder('seller_page_ugc_translate');

    if (areReviewsLoading) {
        return <ReviewsSkeleton />;
    }

    if (!reviews || !showBuyingReviews) {
        return null;
    }

    return (
        <ErrorBoundary onError={onRenderError}>
            <ReviewsCarousel key={reviewsKey} initialState={reviewsBaseConfig} translationUrl={reviewsTranslationUrl} />
        </ErrorBoundary>
    );
};

function selectSellerSlice(seller: SellerPageFederatedSeller) {
    return {
        approvedGigsCount: seller.approvedGigsCount,
    };
}
